import {
  createPublicClient,
  createWalletClient,
  custom,
  defineChain,
  http,
  type Address,
  type EIP1193Provider
} from 'viem';

declare global {
  interface Window { ethereum?: EIP1193Provider }
}

const chainId = Number(import.meta.env.VITE_CHAIN_ID ?? 2221);
const rpcUrl = import.meta.env.VITE_RPC_URL ?? 'http://127.0.0.1:8545';
const explorerUrl = import.meta.env.VITE_EXPLORER_URL || undefined;

export const nstChain = defineChain({
  id: chainId,
  name: import.meta.env.VITE_CHAIN_NAME ?? 'NSTChain Testnet',
  nativeCurrency: {
    name: import.meta.env.VITE_NATIVE_SYMBOL ?? 'akava',
    symbol: import.meta.env.VITE_NATIVE_SYMBOL ?? 'akava',
    decimals: 18
  },
  rpcUrls: { default: { http: [rpcUrl] } },
  ...(explorerUrl ? { blockExplorers: { default: { name: 'Explorer', url: explorerUrl } } } : {})
});

export const publicClient = createPublicClient({ chain: nstChain, transport: http(rpcUrl) });

export function getInjectedProvider() {
  if (!window.ethereum) throw new Error('未检测到钱包，请在支持 EVM 的钱包 DApp 浏览器中打开');
  return window.ethereum;
}

export async function connectWallet(): Promise<Address> {
  const provider = getInjectedProvider();
  const chainHex = `0x${chainId.toString(16)}`;
  const currentChain = await provider.request({ method: 'eth_chainId' });
  if (currentChain !== chainHex) {
    try {
      await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: chainHex }] });
    } catch (error) {
      const code = (error as { code?: number }).code;
      if (code !== 4902) throw new Error('请在钱包中切换至 NSTChain 网络');
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: chainHex,
          chainName: nstChain.name,
          nativeCurrency: nstChain.nativeCurrency,
          rpcUrls: [rpcUrl],
          ...(explorerUrl ? { blockExplorerUrls: [explorerUrl] } : {})
        }]
      });
    }
  }
  const client = createWalletClient({ chain: nstChain, transport: custom(provider) });
  const [address] = await client.requestAddresses();
  if (!address) throw new Error('钱包没有返回可用账户');
  return address;
}

export async function signLogin(address: Address, message: string) {
  const client = createWalletClient({ account: address, chain: nstChain, transport: custom(getInjectedProvider()) });
  return client.signMessage({ account: address, message });
}

export function walletClient(address: Address) {
  return createWalletClient({ account: address, chain: nstChain, transport: custom(getInjectedProvider()) });
}
